import React from 'react';
import { motion } from 'framer-motion';
import { 
  ChartPieIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';

const BenefitsOverview = () => {
  // Mock data - à remplacer par des données API
  const financialData = {
    totalRevenue: 4750000,
    totalExpenses: 2850000,
    netProfit: 1900000,
    profitMargin: 40,
    revenueGrowth: 12,
    period: 'Juillet 2023'
  };

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/20 backdrop-blur-sm rounded-xl p-6 transition-all hover:from-blue-500/30 hover:to-blue-600/30"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-300 text-sm">Revenus Totaux</p>
              <h3 className="text-2xl font-bold text-white mt-1">{financialData.totalRevenue.toLocaleString('fr-DZ')} DA</h3>
            </div>
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-blue-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
            <span className="text-green-400 text-sm ml-1">+{financialData.revenueGrowth}%</span>
            <span className="text-white/50 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-gradient-to-br from-red-500/20 to-red-600/20 border border-red-500/20 backdrop-blur-sm rounded-xl p-6 transition-all hover:from-red-500/30 hover:to-red-600/30"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-red-300 text-sm">Dépenses Totales</p>
              <h3 className="text-2xl font-bold text-white mt-1">{financialData.totalExpenses.toLocaleString('fr-DZ')} DA</h3>
            </div>
            <div className="p-2 bg-red-500/20 rounded-lg">
              <ArrowTrendingDownIcon className="h-6 w-6 text-red-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <ArrowTrendingDownIcon className="h-4 w-4 text-red-400" />
            <span className="text-red-400 text-sm ml-1">-5%</span>
            <span className="text-white/50 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/20 backdrop-blur-sm rounded-xl p-6 transition-all hover:from-green-500/30 hover:to-green-600/30"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-green-300 text-sm">Bénéfice Net</p>
              <h3 className="text-2xl font-bold text-white mt-1">{financialData.netProfit.toLocaleString('fr-DZ')} DA</h3>
            </div>
            <div className="p-2 bg-green-500/20 rounded-lg">
              <ChartPieIcon className="h-6 w-6 text-green-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
            <span className="text-green-400 text-sm ml-1">+15%</span>
            <span className="text-white/50 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/20 backdrop-blur-sm rounded-xl p-6 transition-all hover:from-purple-500/30 hover:to-purple-600/30"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-300 text-sm">Marge Bénéficiaire</p>
              <h3 className="text-2xl font-bold text-white mt-1">{financialData.profitMargin}%</h3>
            </div>
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-purple-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
            <span className="text-green-400 text-sm ml-1">+2%</span>
            <span className="text-white/50 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>
      </div>

      {/* Information Panel */}
      <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
        <div className="flex items-center mb-4">
          <ChartPieIcon className="h-6 w-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Aperçu financier</h2>
        </div>
        <p className="text-white/70 mb-4">
          Cette section affichera un aperçu complet des bénéfices de votre salle de sport. Les graphiques et analyses détaillées seront ajoutés dans la version complète.
        </p>
        
        <div className="bg-white/5 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-white mb-2">Période actuelle: {financialData.period}</h3>
          <p className="text-white/60">
            Pour cette période, votre salle de musculation a généré {financialData.totalRevenue.toLocaleString('fr-DZ')} DA de revenus avec {financialData.totalExpenses.toLocaleString('fr-DZ')} DA de dépenses, 
            résultant en un bénéfice net de {financialData.netProfit.toLocaleString('fr-DZ')} DA et une marge bénéficiaire de {financialData.profitMargin}%.
          </p>
        </div>
      </div>

      {/* Placeholder for Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 h-72 flex items-center justify-center">
          <div className="text-center">
            <ChartPieIcon className="h-12 w-12 text-white/20 mx-auto" />
            <p className="mt-4 text-white/50">Graphique de répartition des revenus</p>
          </div>
        </div>
        
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 h-72 flex items-center justify-center">
          <div className="text-center">
            <ArrowTrendingUpIcon className="h-12 w-12 text-white/20 mx-auto" />
            <p className="mt-4 text-white/50">Graphique d'évolution mensuelle</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenefitsOverview; 